import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  AuditTrailReport,
  AuditTrailFilterParams,
  AuditTrailListResponse,
  EventTypeOption,
  ApiResponse,
} from '@/types/audit-trail.types';

/**
 * Error Handler
 */
interface ApiErrorResponse {
  response?: {
    data?: {
      message?: string;
    };
  };
}

function handleError(error: unknown, defaultMessage: string): never {
  const apiError = error as ApiErrorResponse;
  const message = apiError.response?.data?.message || defaultMessage;
  throw new Error(message);
}

/**
 * Audit Trail Report Service
 * Provides methods for fetching audit trail data
 */
class AuditTrailService {
  /**
   * Get list of audit trail reports with filters and pagination
   * POST /audit-trail-report-srv/utils/list
   */
  async getAuditTrailReports(
    filters: AuditTrailFilterParams = {}
  ): Promise<AuditTrailListResponse> {
    try {
      const response = await apiClient.post<ApiResponse<AuditTrailListResponse>>(
        API_ENDPOINTS.AUDIT_TRAIL.LIST,
        filters
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch audit trail reports');
    }
  }

  /**
   * Get single audit trail report by ID
   * GET /audit-trail-report-srv/:reportId
   */
  async getAuditTrailReportById(reportId: string): Promise<AuditTrailReport> {
    try {
      const response = await apiClient.get<ApiResponse<AuditTrailReport>>(
        API_ENDPOINTS.AUDIT_TRAIL.GET_BY_ID(reportId)
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch audit trail report');
    }
  }

  /**
   * Get list of all event types
   * GET /audit-trail-report-srv/utils/event-types
   */
  async getEventTypes(): Promise<string[]> {
    try {
      const response = await apiClient.get<ApiResponse<string[]>>(
        API_ENDPOINTS.AUDIT_TRAIL.EVENT_TYPES
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch event types');
    }
  }

  /**
   * Get event types formatted for dropdown
   * GET /audit-trail-report-srv/utils/event-dropdown
   */
  async getEventDropdownOptions(): Promise<EventTypeOption[]> {
    try {
      const response = await apiClient.get<ApiResponse<EventTypeOption[]>>(
        API_ENDPOINTS.AUDIT_TRAIL.EVENT_DROPDOWN
      );
      return response.data.data;
    } catch (error) {
      handleError(error, 'Failed to fetch event dropdown options');
    }
  }

  /**
   * Export audit trail reports
   * POST /api/admin/export/audit-trail
   */
  async exportAuditTrailReports(
    filters: Omit<AuditTrailFilterParams, 'skip' | 'limit'> = {}
  ): Promise<Blob> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.AUDIT_TRAIL.EXPORT,
        filters,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      handleError(error, 'Failed to export audit trail reports');
    }
  }
}

export const auditTrailService = new AuditTrailService();
